import type { MutableRefObject } from "react";

import type { AutoCompleteOption } from "../types";

const DEFAULT_MAX_VISIBLE_TAGS = 3;

interface UseAutoCompleteMultiParams {
  options: AutoCompleteOption[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  readOnly: boolean;
  setQuery: (query: string) => void;
  customItemsRef: MutableRefObject<Map<string, AutoCompleteOption>>;
  maxVisibleTags?: number;
}

export const useAutoCompleteMulti = ({
  options,
  selectedValues,
  onSelectionChange,
  readOnly,
  setQuery,
  customItemsRef,
  maxVisibleTags = DEFAULT_MAX_VISIBLE_TAGS,
}: UseAutoCompleteMultiParams) => {
  const selectedItems = selectedValues.map(v => {
    const fromOptions = options.find((o: AutoCompleteOption) => o.id === v);
    if (fromOptions) {
      return fromOptions;
    }
    const cached = customItemsRef.current.get(v);
    if (cached) {
      return cached;
    }
    const created: AutoCompleteOption = { id: v, value: v, label: v };
    customItemsRef.current.set(v, created);
    return created;
  });

  const handleMultiComboboxChange = (items: AutoCompleteOption[]) => {
    const nextIds: string[] = [];
    const seen = new Set<string>();

    for (const item of items) {
      if (!options.some((option: AutoCompleteOption) => option.id === item.id)) {
        customItemsRef.current.set(item.id, item);
      }

      if (seen.has(item.id)) {
        const existingIndex = nextIds.indexOf(item.id);
        if (existingIndex >= 0) {
          nextIds.splice(existingIndex, 1);
        }
        continue;
      }

      seen.add(item.id);
      nextIds.push(item.id);
    }

    onSelectionChange(nextIds);
    setQuery("");
  };

  const handleMultiRemove = (valueToRemove: string) => {
    if (readOnly) {
      return;
    }
    onSelectionChange(selectedValues.filter(v => v !== valueToRemove));
  };

  const visibleTags = selectedValues.slice(0, maxVisibleTags);
  const hiddenTags = selectedValues.slice(maxVisibleTags);
  const hasOverflow = hiddenTags.length > 0;

  return {
    selectedItems,
    handleMultiComboboxChange,
    handleMultiRemove,
    visibleTags,
    hiddenTags,
    hasOverflow,
  };
};
